import { Button, cn } from '@design-system';
import { useAnimationPhases } from '@/features/client-onboarding';
import { useSelector } from 'react-redux';
import { selectClient } from '@/model';
import { CostFactorInsightCard } from '@/components/CostFactorInsightCard';
import { AnimatedCostDisplay } from '@/components/AnimatedCostDisplay';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';
import { useLtcCostDisplayPrecision } from '@client/hooks/useLtcCostDisplayPrecision';
import { SlideOutSheet, EditIconButton } from '@/components';
import EditHealthCareInflationModal from '@/components/EditHealthCareInflationModal';
import { HealthcareInflationCitation } from '@/components/HealthcareInflationCitation';
import { CostDataMethodologySlideOutContent } from '@/components/LearnMoreSlideOutContent';

import {
  CalendarFoldIcon,
  CalendarHeartIcon,
  ChartNoAxesCombinedIcon,
  CheckCircleIcon,
  CircleDollarSignIcon,
  HeartHandshakeIcon,
  LandmarkIcon,
  MapPinHouseIcon,
  Settings2Icon,
  TagIcon,
  MapPin,
  TrendingUp,
  Heart,
  CircleQuestionMark,
} from 'lucide-react';

import { RotateCcw, SkipForward } from 'lucide-react';

import {
  formatPercent,
  formatThousands,
  formatLtcCostDisplay,
  CareEnvironment,
  isDurationCustom,
  areNumbersClose,
  defaultHealthcareInflation,
  isNullOrUndefined,
  appliedHealthCareInflation,
} from '@shared';
import { useState, useEffect, useRef } from 'react';

const PHASES = [
  { id: 'initial', duration: 1000 },
  { id: 'cost-of-living', duration: 2500 },
  { id: 'inflation', duration: 2500 },
  { id: 'family', duration: 2500 },
] as const;

export function CostFactorsAnimation() {
  const client = useSelector(selectClient)!;
  const roundingStep = useLtcCostDisplayPrecision();
  const {
    allPhaseCosts: {
      allPhaseTotalCost,
      allPhaseInflatedFamilyCareCost,
      allPhaseInflatedProfessionalShareCost,
      allPhaseCareMonthsNeeded,
      allPhaseFamilyProvidedPercent,
      allPhaseFamilyCareHoursProvided,
    },
    intakeSurvey: {
      clientIndependencePreference,
      clientFinancialSecurityPreference,
      clientFamilyInvolvementPreference,
      clientHasStartedLtc,
    },
    clientFirstName,
    clientPhasePredictedStartYears,
    phaseCosts,
    clientCalculationSettings: { healthCareInflationRate: clientHealthCareInflationRate },
    clientLocalCareEnvironmentCosts,
    clientInsights,
  } = client;

  const allPhaseInflatedCost =
    allPhaseInflatedProfessionalShareCost + allPhaseInflatedFamilyCareCost;
  const healthCareInflationContribution = allPhaseInflatedCost - allPhaseTotalCost;

  const defaultInflationRate = defaultHealthcareInflation(client);
  const appliedInflationRate = appliedHealthCareInflation(client);
  const isInflationEdited =
    !isNullOrUndefined(clientHealthCareInflationRate) &&
    !areNumbersClose(clientHealthCareInflationRate, defaultInflationRate);

  const rateLocaleType = clientLocalCareEnvironmentCosts[CareEnvironment.home].rateLocaleType;
  const appliedZipCode = clientLocalCareEnvironmentCosts[CareEnvironment.home].zipCode;
  const careDurationEdited = isDurationCustom(phaseCosts);

  const [editModalOpen, setEditModalOpen] = useState(false);

  const validStartYears = Object.values(clientPhasePredictedStartYears).filter(
    (y): y is number => typeof y === 'number' && y > 0,
  );
  const projectedStartYear = validStartYears.length > 0 ? Math.min(...validStartYears) : 0;
  const projectedEndYear =
    projectedStartYear > 0 ? projectedStartYear + Number(allPhaseCareMonthsNeeded ?? 0) / 12 : 0;

  const costOfLivingInsight = clientInsights?.find(i => i.id === 'cost-of-living');
  const inflationInsight = clientInsights?.find(i => i.id === 'inflation-horizon');
  const familyInsight = clientInsights?.find(i => i.id === 'family-caregiving');

  const { currentPhaseIndex, isComplete, replay, skip, isAutoPlaying } = useAnimationPhases({
    phases: PHASES,
  });

  // Cumulative running total based on visible cards
  const runningTotal =
    currentPhaseIndex >= 3
      ? allPhaseInflatedProfessionalShareCost
      : currentPhaseIndex >= 2
        ? allPhaseTotalCost + healthCareInflationContribution
        : currentPhaseIndex >= 1
          ? allPhaseTotalCost
          : 0;

  const showDashes = currentPhaseIndex === 0;

  // Delta indicator: shows +$X or -$X floating up when total changes
  const prevPhaseRef = useRef(currentPhaseIndex);
  const [delta, setDelta] = useState<{ amount: number; direction: 'increase' | 'decrease'; label: string; key: number } | null>(null);

  useEffect(() => {
    if (currentPhaseIndex === prevPhaseRef.current) return;

    const deltas: { amount: number; direction: 'increase' | 'decrease'; label: string }[] = [
      { amount: 0, direction: 'increase', label: '' },
      { amount: allPhaseTotalCost, direction: 'increase', label: 'Cost of care' },
      { amount: healthCareInflationContribution, direction: 'increase', label: 'Inflation adjustment' },
      { amount: allPhaseInflatedFamilyCareCost, direction: 'decrease', label: 'Family caregiving offset' },
    ];

    const d = deltas[currentPhaseIndex];
    if (d && d.amount > 0) {
      setDelta({ ...d, key: Date.now() });
      const timer = setTimeout(() => setDelta(null), 2000);
      prevPhaseRef.current = currentPhaseIndex;
      return () => clearTimeout(timer);
    }

    prevPhaseRef.current = currentPhaseIndex;
  }, [currentPhaseIndex, allPhaseTotalCost, healthCareInflationContribution, allPhaseInflatedFamilyCareCost]);

  const cardConfigs = [
    {
      phaseIndex: 1,
      insight: costOfLivingInsight,
      icon: <MapPin className="text-primary size-5" />,
      cost: allPhaseTotalCost,
      changeType: 'increase' as const,
      expandedContent: (
        <CostOfCareDetail
          clientFirstName={clientFirstName}
          rateLocaleType={rateLocaleType}
          appliedZipCode={appliedZipCode}
          ltcDurationYears={allPhaseCareMonthsNeeded / 12}
          clientIndependencePreference={clientIndependencePreference}
          clientFinancialSecurityPreference={clientFinancialSecurityPreference}
          clientHasStartedLtc={clientHasStartedLtc}
          careDurationEdited={careDurationEdited}
        />
      ),
    },
    {
      phaseIndex: 2,
      insight: inflationInsight,
      icon: <TrendingUp className="text-primary size-5" />,
      cost: healthCareInflationContribution,
      changeType: 'increase' as const,
      expandedContent: (
        <HealthcareInflationDetail
          client={client}
          currentCost={allPhaseTotalCost}
          projectedStartYear={Math.round(projectedStartYear)}
          projectedEndYear={Math.round(projectedEndYear)}
          appliedInflationRate={appliedInflationRate}
          isInflationEdited={isInflationEdited}
          healthCareInflationContribution={healthCareInflationContribution}
          setEditModalOpen={setEditModalOpen}
        />
      ),
    },
    {
      phaseIndex: 3,
      insight: familyInsight,
      icon: <Heart className="text-primary size-5" />,
      cost: allPhaseInflatedFamilyCareCost,
      changeType: 'decrease' as const,
      expandedContent: (
        <FamilyCaregivingDetail
          clientFirstName={clientFirstName}
          clientFamilyInvolvementPreference={clientFamilyInvolvementPreference}
          allPhaseFamilyProvidedPercent={allPhaseFamilyProvidedPercent}
          allPhaseFamilyCareHoursProvided={allPhaseFamilyCareHoursProvided}
          allPhaseInflatedFamilyCareCost={allPhaseInflatedFamilyCareCost}
          rateLocaleType={rateLocaleType}
          appliedZipCode={appliedZipCode}
        />
      ),
    },
  ];

  return (
    <>
      <div
        className="bg-primary/5 border-primary/20 mt-6 flex items-center justify-between rounded-xl border px-6 py-5"
        data-testid="cost-factors-total"
      >
        <div>
          <p className="text-sm font-medium text-gray-600">Estimated total costs</p>
          <p className="text-muted-foreground text-xs">*without a funding plan</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <p
            className={cn(
              'text-2xl font-bold transition-colors duration-700 md:text-3xl',
              isComplete ? 'text-primary-dark' : 'text-gray-600',
            )}
            data-testid="cost-factors-total-value"
          >
            {showDashes ? (
              <span className="text-base tracking-widest text-gray-300">——</span>
            ) : (
              <AnimatedCostDisplay
                value={runningTotal}
                showPreciseTooltip={isComplete}
              />
            )}
          </p>
          <div className="relative h-5">
            {delta && (
              <span
                key={delta.key}
                className={cn(
                  'absolute right-0 flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold animate-delta-float',
                  delta.direction === 'increase' ? 'text-red-600' : 'text-green-600',
                )}
              >
                <span className="text-xs font-medium text-gray-500">{delta.label}</span>
                {delta.direction === 'increase' ? '+' : '-'}
                {formatLtcCostDisplay(delta.amount, { roundingStep }).formattedDisplay}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-4">
        {cardConfigs.map(({ phaseIndex, insight, icon, cost, changeType, expandedContent }) => {
          if (!insight) return null;
          const isVisible = currentPhaseIndex >= phaseIndex;

          return (
            <div
              key={insight.id}
              className={cn(
                'translate-y-8 opacity-0 transition-all duration-700 ease-out',
                { 'translate-y-0 opacity-100': isVisible },
              )}
            >
              <CostFactorInsightCard
                insight={insight}
                icon={icon}
                cost={cost}
                changeType={changeType}
                expandedContent={expandedContent}
              />
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex w-full items-center justify-end gap-4">
        {isAutoPlaying && (
          <Button size="sm" variant="outline" onClick={skip}>
            <SkipForward />
            Skip
          </Button>
        )}
        {isComplete && (
          <Button size="sm" variant="outline" onClick={replay}>
            <RotateCcw />
            Replay
          </Button>
        )}
      </div>

      <EditHealthCareInflationModal open={editModalOpen} setOpen={setEditModalOpen} />
    </>
  );
}

// ── Detail sub-components (same as CostFactorInsightCards) ──────────────────

function localCostString(rateLocaleType: RateLocaleType, zipCode: string) {
  switch (rateLocaleType) {
    case 'National':
      return 'The US';
    case 'State':
      return 'Your State';
    case 'Local':
      return `Zip Code ${zipCode}`;
  }
}

function DetailRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="bg-muted grid size-10 shrink-0 place-items-center rounded-lg text-gray-950 md:size-11">
        {icon}
      </div>
      <div className="flex flex-col">
        <span className="text-foreground text-sm">{label}</span>
        <span className="text-sm font-medium text-gray-950 md:text-base">{value}</span>
      </div>
    </div>
  );
}

function PreferencesRow({
  icon,
  label,
  values,
}: {
  icon: React.ReactNode;
  label: string;
  values: string[];
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="bg-muted grid size-10 shrink-0 place-items-center rounded-lg text-gray-950 md:size-11">
        {icon}
      </div>
      <div className="flex flex-col">
        <span className="text-foreground text-sm">{label}</span>
        <div className="mt-2 flex flex-col gap-2">
          {values.map((el, i) => (
            <span key={i} className="flex items-baseline gap-1.5 text-sm text-gray-950">
              <CheckCircleIcon className="size-3 shrink-0 translate-y-px" />
              {el}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function CostOfCareDetail({
  clientFirstName,
  rateLocaleType,
  appliedZipCode,
  ltcDurationYears,
  clientIndependencePreference,
  clientFinancialSecurityPreference,
  clientHasStartedLtc,
  careDurationEdited,
}: {
  clientFirstName: string;
  rateLocaleType: RateLocaleType;
  appliedZipCode: string;
  ltcDurationYears: number;
  clientIndependencePreference: string;
  clientFinancialSecurityPreference: string;
  clientHasStartedLtc: boolean;
  careDurationEdited: boolean;
}) {
  return (
    <div className="space-y-4">
      <DetailRow
        icon={<CalendarFoldIcon className="size-4 md:size-5" />}
        label={`${careDurationEdited ? 'Edited' : 'Projected'} care timeline`}
        value={`${formatThousands(ltcDurationYears, '', 1)} years ${clientHasStartedLtc ? 'remaining' : ''}`}
      />
      <DetailRow
        icon={<MapPinHouseIcon className="size-4 md:size-5" />}
        label="Market rates for care in"
        value={
          <span className="flex items-center gap-1">
            {localCostString(rateLocaleType, appliedZipCode)}
            <SlideOutSheet
              slideOutContent={<CostDataMethodologySlideOutContent />}
              title="Cost Data"
            >
              <CircleQuestionMark className="text-foreground hover:text-foreground size-3 -translate-y-0.5 hover:cursor-pointer" />
            </SlideOutSheet>
          </span>
        }
      />
      {(clientIndependencePreference || clientFinancialSecurityPreference) && (
        <PreferencesRow
          icon={<Settings2Icon className="size-4 md:size-5" />}
          label={`${clientFirstName}'s preferences:`}
          values={[clientIndependencePreference, clientFinancialSecurityPreference].filter(Boolean)}
        />
      )}
    </div>
  );
}

function HealthcareInflationDetail({
  client,
  currentCost,
  projectedStartYear,
  projectedEndYear,
  appliedInflationRate,
  isInflationEdited,
  healthCareInflationContribution,
  setEditModalOpen,
}: {
  client: Client;
  currentCost: number;
  projectedStartYear: number;
  projectedEndYear: number;
  appliedInflationRate: number;
  isInflationEdited: boolean;
  healthCareInflationContribution: number;
  setEditModalOpen: (open: boolean) => void;
}) {
  return (
    <div className="space-y-4">
      <DetailRow
        icon={<TagIcon className="size-4 md:size-5" />}
        label="Current cost:"
        value={<LtcCostDisplay value={currentCost} />}
      />
      <DetailRow
        icon={<CalendarHeartIcon className="size-4 md:size-5" />}
        label="Projected care start to end year:"
        value={projectedStartYear > 0 ? `${projectedStartYear} - ${projectedEndYear}` : '-'}
      />
      <DetailRow
        icon={<LandmarkIcon className="size-4 md:size-5" />}
        label={`${isInflationEdited ? 'Edited healthcare inflation\u00B9:' : 'Healthcare inflation\u00B9:'}`}
        value={
          <span className="flex items-center">
            <span>{`${formatPercent(appliedInflationRate, 1)} annually`}</span>
            <EditIconButton
              onClick={() => setEditModalOpen(true)}
              tooltipText="Edit healthcare inflation rate"
            />
          </span>
        }
      />
      <DetailRow
        icon={<ChartNoAxesCombinedIcon className="size-4 md:size-5" />}
        label="Future projected cost²:"
        value={
          <LtcCostDisplay value={currentCost + healthCareInflationContribution} />
        }
      />
      <div className="text-muted-foreground mt-2 space-y-1 text-xs leading-snug">
        <p>
          2. <HealthcareInflationCitation client={client} />
        </p>
        <p>
          3. This simplified explanation is for educational purposes only and may slightly
          overestimate the actual calculation used to estimate cost.
        </p>
      </div>
    </div>
  );
}

function FamilyCaregivingDetail({
  clientFirstName,
  clientFamilyInvolvementPreference,
  allPhaseFamilyProvidedPercent,
  allPhaseFamilyCareHoursProvided,
  allPhaseInflatedFamilyCareCost,
  rateLocaleType,
  appliedZipCode,
}: {
  clientFirstName: string;
  clientFamilyInvolvementPreference: string;
  allPhaseFamilyProvidedPercent: number;
  allPhaseFamilyCareHoursProvided: number;
  allPhaseInflatedFamilyCareCost: number;
  rateLocaleType: RateLocaleType;
  appliedZipCode: string;
}) {
  return (
    <div className="space-y-4">
      <DetailRow
        icon={<HeartHandshakeIcon className="size-4 md:size-5" />}
        label="Projected family caregiving:"
        value={`${formatThousands(allPhaseFamilyCareHoursProvided, '', 0)} hours (${formatPercent(allPhaseFamilyProvidedPercent, 1)} of care needs)`}
      />
      <DetailRow
        icon={<CircleDollarSignIcon className="size-4 md:size-5" />}
        label="Projected value of care provided by family:"
        value={<LtcCostDisplay value={allPhaseInflatedFamilyCareCost} />}
      />
      <DetailRow
        icon={<MapPinHouseIcon className="size-4 md:size-5" />}
        label="Based on market rates for home care in"
        value={localCostString(rateLocaleType, appliedZipCode)}
      />
      {clientFamilyInvolvementPreference && (
        <PreferencesRow
          icon={<Settings2Icon className="size-4 md:size-5" />}
          label={`${clientFirstName}'s preferences:`}
          values={[clientFamilyInvolvementPreference]}
        />
      )}
    </div>
  );
}
